import React, { useState } from "react";
import styles from "./CreateFeature.module.css";
import { FiX, FiPlus, FiAlertCircle, FiHelpCircle } from "react-icons/fi";
import { RotatingLines } from "react-loader-spinner";
import ContextSelector from "Components/FeatureFlip/Common/ContextSelector/ContextSelector";
import { useFeatureFlipStore } from "Store/featureFlipStore";
import { useCreateFeature } from "API/Queries/useCreateFeatures";
import {
  CreateFeaturePayload,
  FEATURE_TYPE_INFO,
  FeatureType,
} from "Interfaces/FeatureFlip";
import { useCreateTreatments } from "API/Queries/useCreateTreatments";
import { useGetAllFeatures } from "API/Queries/useGetAllFeatures";
import RequiredStar from "Components/Common/RequiredStar";
import { emptyStringsToNull } from "Utils/utils";

interface CreateFeatureProps {
  setShowFeatureForm: React.Dispatch<React.SetStateAction<boolean>>;
}

const initialFormData: CreateFeaturePayload = {
  featureName: "",
  description: "",
  featureType: "RELEASE",
  enabled: false,
  isDynamic: false,
};

const FEATURE_TYPES: FeatureType[] = [
  "RELEASE",
  "EXPERIMENT",
  "PERMISSION",
  "OPS",
];

// Tooltip component
const Tooltip = ({ content }: { content: string }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className={styles.tooltipContainer}>
      <span
        className={styles.tooltipIcon}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <FiHelpCircle />
      </span>
      {showTooltip && <div className={styles.tooltipContent}>{content}</div>}
    </div>
  );
};

const CreateFeature = ({ setShowFeatureForm }: CreateFeatureProps) => {
  const [formData, setFormData] =
    useState<CreateFeaturePayload>(initialFormData);

  const [error, setError] = useState<string>();
  const { mutateAsync: createFeatureAsync, isLoading: isCreatingFeature } =
    useCreateFeature();
  const { mutateAsync: createTreatmentsAsync, isLoading: isCreatingTreatment } =
    useCreateTreatments();
  const isLoading = isCreatingFeature || isCreatingTreatment;
  const selectedProjectId = useFeatureFlipStore((s) => s.selectedProjectId);

  const setHasMoreDataFeatures = useFeatureFlipStore(
    (s) => s.setHasMoreDataFeatures
  );
  const setPageFeatures = useFeatureFlipStore((s) => s.setPageFeatures);
  const setFeatures = useFeatureFlipStore((s) => s.setFeatures);

  const { refetch: getAllFeatures } = useGetAllFeatures(
    { query: `projectId==${selectedProjectId}` },
    {
      onSuccess: (features) => {
        window.scroll(0, 0);
        setFeatures(features);
        setPageFeatures(1);

        if (features.length === 10) {
          setHasMoreDataFeatures(true);
        }
      },
    }
  );

  const resetForm = () => {
    setFormData(initialFormData);
    setError("");
  };

  const shouldDisableButton = (): boolean => {
    const { featureName } = formData;

    const isFeatureNameEmpty = !featureName.trim();

    return isFeatureNameEmpty || !selectedProjectId;
  };

  const handleCreateFeature = async () => {
    if (!selectedProjectId) return;

    const payload = emptyStringsToNull(formData) as CreateFeaturePayload;

    try {
      const feature = await createFeatureAsync({
        ...payload,
        projectId: selectedProjectId,
      });

      if (!feature?.id) throw new Error("Feature creation failed");

      await Promise.all([
        createTreatmentsAsync({
          projectId: selectedProjectId,
          featureId: feature.id,
          treatmentName: "on",
          rolloutPercentage: formData.isDynamic ? 50 : 0,
          default: false,
        }),
        createTreatmentsAsync({
          projectId: selectedProjectId,
          featureId: feature.id,
          treatmentName: "off",
          rolloutPercentage: formData.isDynamic ? 50 : 0,
          default: true,
        }),
      ]);
      await getAllFeatures();
      resetForm();
      setShowFeatureForm(false);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Something went wrong";
      setError(errorMessage);
    }
  };

  const handleDynamicChange = (isDynamic: boolean) => {
    setFormData((prev) => ({
      ...prev,
      isDynamic,
    }));
  };

  const handleEnabledChange = (enabled: boolean) => {
    setFormData((prev) => ({
      ...prev,
      enabled,
    }));
  };

  return (
    <div className={styles.modal}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2>Create New Feature</h2>
          {!isLoading && (
            <button
              className={styles.closeButton}
              onClick={() => {
                setShowFeatureForm(false);
                resetForm();
              }}
            >
              <FiX />
            </button>
          )}
        </div>

        <div className={styles.modalBody}>
          <div className={styles.formGroup}>
            <label>
              Name
              <RequiredStar />
            </label>
            <input
              type="text"
              value={formData.featureName}
              onChange={(e) =>
                setFormData({ ...formData, featureName: e.target.value })
              }
              placeholder="e.g., dark_mode"
              className={styles.input}
              required
              disabled={isLoading}
            />
          </div>

          <div className={styles.formGroup}>
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Describe what this feature does"
              className={styles.textarea}
              required
              disabled={isLoading}
            />
          </div>

          <div className={styles.formGroup}>
            <div className={styles.labelWithTooltip}>
              <label>
                Type
                <RequiredStar />
              </label>
              <Tooltip content={FEATURE_TYPE_INFO[formData.featureType]} />
            </div>
            <select
              value={formData.featureType}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  featureType: e.target.value as FeatureType,
                })
              }
              className={styles.select}
              disabled={isLoading}
            >
              {FEATURE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.radioGroup}>
            <div className={styles.labelWithTooltip}>
              <label>
                Dynamic
                <RequiredStar />
              </label>
              <Tooltip content="Used when we want to do scale ups like 10% - 50% - 100%" />
            </div>
            <div className={styles.radioOptions}>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  checked={!formData.isDynamic}
                  onChange={() => handleDynamicChange(false)}
                  disabled={isLoading}
                />
                <span>No</span>
              </label>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  checked={formData.isDynamic}
                  onChange={() => handleDynamicChange(true)}
                  disabled={isLoading}
                />
                <span>Yes</span>
              </label>
            </div>
          </div>

          <div className={styles.radioGroup}>
            <div className={styles.labelWithTooltip}>
              <label>
                Enabled
                <RequiredStar />
              </label>
              <Tooltip content="To turn on/off the feature" />
            </div>
            <div className={styles.radioOptions}>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  checked={!formData.enabled}
                  onChange={() => handleEnabledChange(false)}
                  disabled={isLoading}
                />
                <span>No</span>
              </label>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  checked={formData.enabled}
                  onChange={() => handleEnabledChange(true)}
                  disabled={isLoading}
                />
                <span>Yes</span>
              </label>
            </div>
          </div>
        </div>

        {error && (
          <p className={styles.error}>
            <FiAlertCircle />
            {error}
          </p>
        )}

        <div className={styles.modalFooter}>
          <button
            onClick={handleCreateFeature}
            className={styles.saveButton}
            disabled={shouldDisableButton()}
          >
            {isLoading ? (
              <>
                <RotatingLines
                  strokeColor="white"
                  strokeWidth="5"
                  animationDuration="0.75"
                  width="20"
                  visible={true}
                />
                Creating...
              </>
            ) : (
              <>
                <FiPlus /> Create Feature
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateFeature;
